import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Seo from "../../components/Seo";
import { PageHeader, PickerField } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, isBlankFile, userService } from "../../services/userApi";

const PHONE_RE = /^\+?[0-9]{7,15}$/;

// Label above, value below — one row of the white card, as on the app.
const FormRow = ({ label, children }) => (
  <div className="usp-form-row">
    <span className="usp-form-label">{label}</span>
    {children}
  </div>
);

// "Edit Profile": photo, name, phone, country, nationality and date of birth.
// Email is the login identity, so it's shown read-only (the app re-verifies
// email changes with an OTP).
const UserEditProfile = () => {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useOutletContext();
  const pictureRef = useRef(null);
  const [form, setForm] = useState({ name: "", phone: "", country: "", nationality: "", dob: "" });
  const [picture, setPicture] = useState(null);
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);
  const loadedFor = useRef(null);

  // Fill the form once the profile is known (the layout may still be loading it).
  useEffect(() => {
    if (!profile?.id || loadedFor.current === profile.id) return;
    loadedFor.current = profile.id;
    setForm({
      name: profile.name || "",
      phone: profile.phone || "",
      country: profile.country || "",
      nationality: profile.nationality || "",
      dob: profile.dob && moment(profile.dob).isValid() ? moment(profile.dob).format("YYYY-MM-DD") : "",
    });
  }, [profile]);

  useEffect(() => {
    userService
      .getDropdownData()
      .then((res) => setCountries((res?.data?.data?.countries || []).map((c) => ({ value: c.name, label: c.name }))))
      .catch(() => toast.error("Couldn't load the country list."));
  }, []);

  // Keep a saved value selectable even if it isn't in the lookup list.
  const countryOptions = useMemo(() => {
    const extra = [form.country, form.nationality].filter((v) => v && !countries.some((o) => o.value === v));
    return [...countries, ...[...new Set(extra)].map((v) => ({ value: v, label: v }))];
  }, [countries, form.country, form.nationality]);

  const localPreview = useMemo(() => (picture ? URL.createObjectURL(picture) : null), [picture]);
  useEffect(() => () => localPreview && URL.revokeObjectURL(localPreview), [localPreview]);
  const preview = localPreview || (!isBlankFile(profile?.picture) ? profile.picture : null);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Images must be 5 MB or smaller.");
      return;
    }
    setPicture(file);
  };

  const save = async () => {
    const phone = form.phone.replace(/[\s-]/g, "");
    if (!form.name.trim()) return toast.error("Full name is required.");
    if (phone && !PHONE_RE.test(phone)) return toast.error("Please enter a valid phone number.");
    setSaving(true);
    try {
      const res = await userService.updateProfile({
        name: form.name.trim(),
        phone: phone || undefined,
        country: form.country || undefined,
        nationality: form.nationality || undefined,
        dob: form.dob || undefined,
        picture: picture || undefined,
      });
      if (!res?.data?.status) throw new Error(res?.data?.message);
      toast.success(res.data.message || "Profile updated");
      await refreshProfile();
      navigate("/user/profile");
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to update profile."));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  return (
    <div className="lwp-page lwp-page-narrow">
      <Seo title="Edit Profile | Legal Platform" noindex />
      <PageHeader
        title="Edit Profile"
        backTo="/user/profile"
        actions={
          <button type="button" className="usp-save-btn" onClick={save} disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm" /> : "Save"}
          </button>
        }
      />

      <button type="button" className="usp-photo-edit" onClick={() => pictureRef.current?.click()} aria-label="Change photo">
        {preview && <img src={preview} alt="" />}
        <span className="usp-photo-edit-icon">
          <i className="bi bi-camera-fill" />
        </span>
      </button>
      <input ref={pictureRef} type="file" accept="image/*" hidden onChange={pick} />

      <div className="usp-form-card">
        <FormRow label="Full Name">
          <input
            className="usp-form-input"
            value={form.name}
            onChange={(e) => set("name")(e.target.value)}
            placeholder="Full Name"
            autoComplete="name"
          />
        </FormRow>
        <FormRow label="Email">
          <input className="usp-form-input" value={profile?.email || ""} readOnly aria-readonly="true" />
        </FormRow>
        <FormRow label="Phone Number">
          <input
            type="tel"
            className="usp-form-input"
            value={form.phone}
            onChange={(e) => set("phone")(e.target.value.replace(/[^\d+\s-]/g, ""))}
            placeholder="+971 50 000 0000"
            autoComplete="tel"
          />
        </FormRow>
        <FormRow label="Country">
          <PickerField label="Country" hideLabel placeholder="Country" options={countryOptions} value={form.country} onChange={set("country")} />
        </FormRow>
        <FormRow label="Nationality">
          <PickerField
            label="Nationality"
            hideLabel
            placeholder="Nationality"
            options={countryOptions}
            value={form.nationality}
            onChange={set("nationality")}
          />
        </FormRow>
        <FormRow label="Date of Birth">
          <input
            type="date"
            className={`usp-form-input${form.dob ? "" : " is-empty"}`}
            value={form.dob}
            max={moment().format("YYYY-MM-DD")}
            onChange={(e) => set("dob")(e.target.value)}
            aria-label="Date of Birth"
          />
        </FormRow>
      </div>
    </div>
  );
};

export default UserEditProfile;
